import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { planReviewDate, roleAtLeast } from "@amber/contracts";
import {
  HISTORY_OPTIONS,
  HISTORY_OPTION_LABELS,
  windowStart,
  type HistoryOption,
  type PlanSourceEntry,
  type PlanView,
  type ProfileListRow,
} from "@amber/profiles/contracts";
import { useSession } from "@amber/access/ui";
import {
  AppLink,
  Button,
  Callout,
  Drawer,
  DrawerSection,
  FieldHint,
  FieldLabel,
  ReasonField,
  Select,
  TextArea,
  TextInput,
  formatDateTime,
  isApiError,
  useReason,
  useToast,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import { TIER_LABELS, type HistoryWindowView, type SourceOption } from "../../contracts/index";
import { historyQuery, sourcesQuery, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT, planCommands } from "../queries";
import { HealthLabel, shortDate } from "../sources/SourceVisuals";
import { PersonPauseDialog } from "./PersonPauseDialog";
import { PlanEstimate } from "./PlanEstimate";

type Tier = "priority" | "standard" | "low";

function tierOf(value: string): Tier {
  if (value === "priority") return "priority";
  if (value === "low") return "low";
  return "standard";
}

function planSubtitle(plan: PlanView | null): string {
  if (!plan) return "No plan yet; nothing is monitored";
  if (plan.pending)
    return `Changes pending · revision ${String(plan.latest.revision)} saved ${formatDateTime(plan.latest.recorded_at)} by ${plan.latest.recorded_by}`;
  return `Applied ${plan.applied?.applied_at ? formatDateTime(plan.applied.applied_at) : ""}`;
}

function SourceEntry({
  entry: e,
  title,
  status,
  entries,
  setEntries,
}: {
  entry: PlanSourceEntry;
  title: string;
  status: string | null;
  entries: PlanSourceEntry[];
  setEntries: (next: PlanSourceEntry[]) => void;
}) {
  return (
    <li className="rounded-lg border border-divider p-2">
      <div className="flex items-center gap-2">
        <b className="min-w-0 flex-1 truncate font-strong text-fg">{title}</b>
        {status ? <span className="text-xs text-waiting">{status}</span> : null}
        <Button
          size="sm"
          onClick={() => {
            setEntries(entries.filter((x) => x.source_id !== e.source_id));
          }}
        >
          Remove
        </Button>
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-md">
        <label className="sr-only" htmlFor={`tier-${e.source_id}`}>
          Cadence tier for {title}
        </label>
        <Select
          id={`tier-${e.source_id}`}
          className="h-7 w-[170px]"
          value={e.cadence_tier}
          onChange={(ev) => {
            const tier = tierOf(ev.target.value);
            setEntries(entries.map((x) => (x.source_id === e.source_id ? { ...x, cadence_tier: tier } : x)));
          }}
        >
          <option value="priority">Priority · every 2 h</option>
          <option value="standard">Standard · every 12 h</option>
          <option value="low">Low · every 24 h</option>
        </Select>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            className="accent-[var(--color-link)]"
            checked={e.required}
            onChange={(ev) => {
              setEntries(
                entries.map((x) =>
                  x.source_id === e.source_id
                    ? {
                        ...x,
                        required: ev.target.checked,
                        cadence_tier: ev.target.checked ? "priority" : x.cadence_tier,
                      }
                    : x,
                ),
              );
            }}
          />
          Required route
        </label>
      </div>
    </li>
  );
}

function SourcesSection({
  plan,
  options,
  entries,
  setEntries,
}: {
  plan: PlanView | null;
  options: readonly SourceOption[];
  entries: PlanSourceEntry[];
  setEntries: (next: PlanSourceEntry[]) => void;
}) {
  const [adding, setAdding] = useState("");
  const titleOf = (id: string) => options.find((o) => o.id === id)?.title ?? "Unknown source";
  const addable = options.filter((o) => o.collected && !entries.some((e) => e.source_id === o.id));
  const applied = new Map((plan?.applied?.sources ?? []).map((s) => [s.source_id, s]));
  const statusOf = (e: PlanSourceEntry) => {
    const was = applied.get(e.source_id);
    const changed = !was || was.cadence_tier !== e.cadence_tier || was.required !== e.required;
    if (!changed || !plan) return null;
    return was ? "changed" : "added";
  };
  return (
    <DrawerSection title="Sources">
      {entries.length === 0 ? (
        <p className="m-0 text-md text-muted">No sources. A person with no sources in their plan is not monitored.</p>
      ) : null}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {entries.map((e) => (
          <SourceEntry
            key={e.source_id}
            entry={e}
            title={titleOf(e.source_id)}
            status={statusOf(e)}
            entries={entries}
            setEntries={setEntries}
          />
        ))}
        {[...applied.keys()]
          .filter((id) => !entries.some((e) => e.source_id === id))
          .map((id) => (
            <li key={id} className="rounded-lg border border-dashed border-divider p-2 text-md text-muted">
              <s>{titleOf(id)}</s> · removed; will no longer get new items, nothing already collected is removed
            </li>
          ))}
      </ul>
      <div className="mt-2 flex items-center gap-2">
        <label className="sr-only" htmlFor="plan-add-source">
          Add a source
        </label>
        <Select
          id="plan-add-source"
          value={adding}
          onChange={(e) => {
            setAdding(e.target.value);
          }}
        >
          <option value="">Add a source…</option>
          {addable.map((o) => (
            <option key={o.id} value={o.id}>
              {o.title} · {o.platform_label}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          disabledReason={adding === "" ? "Pick a source first" : null}
          onClick={() => {
            const option = addable.find((o) => o.id === adding);
            if (!option) return;
            setEntries([...entries, { source_id: option.id, cadence_tier: option.default_tier, required: false }]);
            setAdding("");
          }}
        >
          Add
        </Button>
      </div>
    </DrawerSection>
  );
}

interface HistoryDraft {
  history: HistoryOption;
  setHistory: (next: HistoryOption) => void;
  since: string;
  setSince: (next: string) => void;
  review: string;
  setReview: (next: string) => void;
  rationale: string;
  setRationale: (next: string) => void;
}

function HistorySection({
  draft,
  central,
  rationaleError,
}: {
  draft: HistoryDraft;
  central: HistoryWindowView | undefined;
  rationaleError: string | null;
}) {
  const { history, setHistory, since, setSince, review, setReview, rationale, setRationale } = draft;
  return (
    <DrawerSection title="History and review">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="plan-history">History</FieldLabel>
          <Select
            id="plan-history"
            value={history}
            onChange={(e) => {
              setHistory(HISTORY_OPTIONS.find((o) => o === e.target.value) ?? "default");
            }}
          >
            {HISTORY_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {HISTORY_OPTION_LABELS[o]}
              </option>
            ))}
          </Select>
          <FieldHint>
            {history === "default"
              ? `Follows the central window (${central?.window_label ?? "2 weeks"})`
              : "Custom for this person"}
          </FieldHint>
        </div>
        <div>
          <FieldLabel htmlFor="plan-review">Review date</FieldLabel>
          <TextInput
            id="plan-review"
            type="date"
            value={review}
            onChange={(e) => {
              setReview(e.target.value);
            }}
          />
        </div>
        {history === "since" ? (
          <div>
            <FieldLabel htmlFor="plan-since">Collect since</FieldLabel>
            <TextInput
              id="plan-since"
              type="date"
              value={since}
              onChange={(e) => {
                setSince(e.target.value);
              }}
            />
          </div>
        ) : null}
      </div>
      <div className="mt-3">
        <FieldLabel htmlFor="plan-rationale" required>
          Rationale
        </FieldLabel>
        <TextArea
          id="plan-rationale"
          rows={2}
          value={rationale}
          aria-invalid={rationaleError ? true : undefined}
          onChange={(e) => {
            setRationale(e.target.value);
          }}
        />
        {rationaleError ? <p className="m-0 mt-1 text-md text-attention">{rationaleError}</p> : null}
      </div>
    </DrawerSection>
  );
}

function MonitoringNow({ personId, name }: { personId: string; name: string }) {
  const outcomes = useQuery(sourcesQuery({ person: personId }));
  return (
    <DrawerSection title="Monitoring now">
      {outcomes.data && outcomes.data.sources.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-md">
          {outcomes.data.sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-2">
              <AppLink href={`/sources/${s.id}`} className="font-strong text-link">
                {s.title}
              </AppLink>
              <HealthLabel state={s.health.state} label={s.health.label} />
              <span className="text-muted">
                {TIER_LABELS[s.tier]} · complete through {shortDate(s.complete_through)}
                {s.open_gaps > 0 ? ` · ${String(s.open_gaps)} open gap${s.open_gaps === 1 ? "" : "s"}` : ""}
              </span>
              {s.health.state === "not_collected" ||
              (s.health.state === "late" && s.health.reason?.startsWith("No automatic scan route")) ? (
                <span className="basis-full text-muted">Unsupported route: {s.health.reason}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-md text-muted">Nothing is monitored for {name} yet.</p>
      )}
    </DrawerSection>
  );
}

function usePlanDraft(latest: PlanView["latest"] | null) {
  const [entries, setEntries] = useState<PlanSourceEntry[]>(latest?.sources ?? []);
  const [history, setHistory] = useState<HistoryOption>(latest?.history.option ?? "default");
  const [since, setSince] = useState(latest?.history.since ?? "");
  const [review, setReview] = useState(latest?.review_date ?? planReviewDate(new Date()));
  const [rationale, setRationale] = useState(latest?.rationale ?? "");
  return { entries, setEntries, history, setHistory, since, setSince, review, setReview, rationale, setRationale };
}

function backToOf(latest: PlanView["latest"] | null, draft: HistoryDraft, central: HistoryWindowView | undefined) {
  const start = new Date(latest?.monitoring_start ?? Date.now());
  if (draft.history === "since") return draft.since || start.toISOString().slice(0, 10);
  return windowStart(draft.history === "default" ? (central?.window ?? "2_weeks") : draft.history, start)
    .toISOString()
    .slice(0, 10);
}

function PauseButton({ person, onClick }: { person: ProfileListRow; onClick: () => void }) {
  const session = useSession();
  return (
    <Button
      size="sm"
      className="mr-auto"
      disabledReason={
        session && roleAtLeast(session.user.role, "editor")
          ? null
          : "Only Editors and above can pause or resume a person"
      }
      onClick={onClick}
    >
      {person.lifecycle_state === "paused" ? "Resume…" : "Pause…"}
    </Button>
  );
}

export function PlanEditor({
  person,
  personId,
  name,
  plan,
  options,
  onClose,
}: {
  person: ProfileListRow;
  personId: string;
  name: string;
  plan: PlanView | null;
  options: readonly SourceOption[];
  onClose: () => void;
}) {
  const toast = useToast();
  const central = useQuery(historyQuery).data;
  const [pauseOpen, setPauseOpen] = useState(false);
  const latest = plan?.latest ?? null;
  const draft = usePlanDraft(latest);
  const { entries, setEntries, history, since, review, rationale } = draft;
  const reason = useReason();
  const [base, setBase] = useState(latest?.revision ?? null);
  useEffect(() => {
    setBase(latest?.revision ?? null);
  }, [latest?.revision]);
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      planCommands.save(
        personId,
        {
          base_revision: base,
          sources: entries,
          history: { option: history, since: history === "since" ? since : null },
          rationale: rationale.trim(),
          review_date: review,
          languages: latest?.languages ?? [],
          reason: text,
        },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show("Plan saved · Changes pending");
      reason.reset();
    },
  });
  const rationaleError =
    reason.submitted && rationale.trim().length === 0 ? "Say why these sources are monitored" : null;
  const backTo = backToOf(latest, draft, central);
  return (
    <Drawer
      title={`${name} · coverage plan`}
      subtitle={planSubtitle(plan)}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          {person.lifecycle_state === "active" || person.lifecycle_state === "paused" ? (
            <PauseButton
              person={person}
              onClick={() => {
                setPauseOpen(true);
              }}
            />
          ) : null}
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="plan-form"
            aria-busy={mutation.isPending || undefined}
          >
            Save plan
          </Button>
        </span>
      }
    >
      {person.lifecycle_state === "paused" ? (
        <Callout tone="waiting" className="mb-3">
          <b className="font-strong">Paused.</b> Scans requested only for {name} are stopped; shared sources keep
          running for others. Resuming turns the paused interval into a gap and offers catch-up.
        </Callout>
      ) : null}
      {pauseOpen ? (
        <PersonPauseDialog
          person={person}
          onClose={() => {
            setPauseOpen(false);
          }}
        />
      ) : null}
      <form
        id="plan-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (!reason.submit() || rationale.trim().length === 0) return;
          mutation.mutate(reason.trimmed);
        }}
      >
        <SourcesSection plan={plan} options={options} entries={entries} setEntries={setEntries} />
        <HistorySection draft={draft} central={central} rationaleError={rationaleError} />
        <DrawerSection title="Estimate">
          <PlanEstimate entries={entries} backTo={backTo} planId={plan?.id ?? null} />
        </DrawerSection>
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        {mutation.error ? (
          <Callout tone="attention" role="alert">
            {errorText(mutation.error)}
            {isApiError(mutation.error) && mutation.error.code === "conflict" ? (
              <span className="block">The plan reloaded with their version; your edits are kept here.</span>
            ) : null}
          </Callout>
        ) : null}
        <MonitoringNow personId={personId} name={name} />
      </form>
    </Drawer>
  );
}
